<script lang="ts">
  import { onMount } from 'svelte';
  import ArrowUpRight from '@lucide/svelte/icons/arrow-up-right';
  import { Button } from '@cio/ui/base/button';
  import { CONTACT_PATH } from '$lib/utils/site';
  import { scrollToContactForm } from '$lib/utils/contact-scroll';

  let {
    href = CONTACT_PATH,
    label = 'Get started',
    compact = false,
    onActivate
  }: { href?: string; label?: string; compact?: boolean; onActivate?: () => void } = $props();
  let frame = $state<HTMLSpanElement>();

  function handleClick(event: MouseEvent) {
    onActivate?.();
    if (href !== CONTACT_PATH || event.metaKey || event.ctrlKey || event.shiftKey) return;
    if (!scrollToContactForm()) return;

    event.preventDefault();
  }
  let visible = $state(false);
  let documentVisible = $state(true);

  onMount(() => {
    if (!frame) return;

    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
    });
    observer.observe(frame);
    function updateVisibility() {
      documentVisible = !document.hidden;
    }
    document.addEventListener('visibilitychange', updateVisibility);
    updateVisibility();
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', updateVisibility);
    };
  });
</script>

<span class="action-frame" class:compact class:running={visible && documentVisible} bind:this={frame}>
  <span class="action-streak" aria-hidden="true"></span>
  <Button {href} class="motion-action" onclick={handleClick}>
    <span class="label-window"
      ><span class="label-track"><span>{label}</span><span aria-hidden="true">{label}</span></span></span
    >
    <span class="action-arrow"><ArrowUpRight size={compact ? 13 : 17} /></span>
  </Button>
</span>

<style>
  .action-frame {
    position: relative;
    display: inline-flex;
    padding: 2px;
    overflow: hidden;
    border-radius: 10px;
    background: #1447e6;
    isolation: isolate;
    vertical-align: middle;
  }
  .action-streak {
    position: absolute;
    width: 240%;
    aspect-ratio: 1;
    top: 50%;
    left: -70%;
    background: conic-gradient(from 0deg, transparent 35%, #fae9a2 55%, #ffbc94 70%, transparent 82%);
    transform: translateY(-50%);
    animation: border-travel 5s linear infinite;
    animation-play-state: paused;
    pointer-events: none;
  }
  .running .action-streak {
    animation-play-state: running;
  }
  .action-frame :global(.motion-action) {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    min-height: 51px;
    padding: 7px 9px 7px 21px;
    border-radius: 8px;
    background: #1447e6;
    color: #fff;
    border: 0;
    font-family: Geist, sans-serif;
    font-size: 13px;
    font-weight: 500;
    box-shadow: none;
  }
  .label-window {
    height: 20px;
    overflow: hidden;
    display: block;
  }
  .label-track {
    display: grid;
    transition: transform 450ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  .label-track > span {
    height: 20px;
    display: flex;
    align-items: center;
  }
  .action-arrow {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: 6px;
    background: #f4edac;
    color: #17315d;
    transition:
      background 220ms,
      transform 450ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  .action-frame:hover .label-track,
  .action-frame:focus-within .label-track {
    transform: translateY(-20px);
  }
  .action-frame:hover .action-arrow {
    background: #ffcca8;
    transform: rotate(5deg);
  }
  .action-frame:focus-within {
    outline: 3px solid #1447e6;
    outline-offset: 5px;
  }
  .compact :global(.motion-action) {
    min-height: 37px;
    padding: 4px 5px 4px 13px;
    font-size: 11px;
    gap: 15px;
  }
  .compact .action-arrow {
    width: 27px;
    height: 27px;
    border-radius: 5px;
  }
  @keyframes border-travel {
    to {
      transform: translateY(-50%) rotate(360deg);
    }
  }
  :global(html[data-motion='paused']) .action-streak {
    animation-play-state: paused;
  }
  :global(html[data-motion='paused']) .label-track,
  :global(html[data-motion='paused']) .action-arrow {
    transition: none;
    transform: none;
  }
  @media (prefers-reduced-motion: reduce) {
    .action-streak {
      animation: none;
    }
    .label-track,
    .action-arrow {
      transition: none;
      transform: none !important;
    }
  }
  @media (max-width: 760px) {
    .action-frame :global(.motion-action) {
      min-height: 47px;
      font-size: 12px;
      padding-left: 15px;
      gap: 17px;
    }
    .action-arrow {
      width: 31px;
      height: 31px;
    }
    .compact :global(.motion-action) {
      min-height: 34px;
      font-size: 10px;
      padding-left: 10px;
      gap: 10px;
    }
    .compact .action-arrow {
      width: 24px;
      height: 24px;
    }
  }
</style>
